import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Inbox, ChevronLeft, ChevronRight, OctagonMinus, MailCheck } from "lucide-react";

export default function ReplyInbox() {
  const [q, setQ] = useState("");
  const [stopOnly, setStopOnly] = useState(false);
  const [triage, setTriage] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState(null);
  const limit = 25;

  useEffect(() => {
    api.get(`/admin/inbound-emails?q=${encodeURIComponent(q)}&stop_only=${stopOnly}&triage=${triage}&skip=${skip}&limit=${limit}`)
      .then(({ data }) => setData(data)).catch(() => {});
  }, [q, stopOnly, triage, skip]);
  useEffect(() => { setSkip(0); }, [q, stopOnly, triage]);

  const TRIAGE_STYLE = {
    interested: "border-emerald-500 text-emerald-400",
    question: "border-amber-500 text-amber-400",
    not_now: "border-neutral-600 text-neutral-400",
  };
  const tc = data?.triage_counts || {};

  return (
    <div className="px-4 md:px-8 py-8 max-w-5xl" data-testid="hq-reply-inbox-page">
      <div className="text-xs tracking-widest text-volt">ADMIN HQ · OPERATIONS</div>
      <h1 className="heading text-4xl mt-1">REPLY INBOX</h1>
      <p className="text-neutral-400 mt-2 text-sm max-w-2xl">
        Every reply caught by the relay at reply.coachconnect.ai — forwarded copies also land in your Gmail.
        STOP replies are auto-opted-out before they ever reach you.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <input data-testid="inbox-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search sender, subject, text…"
          className="bg-black border border-neutral-800 px-3 py-2 text-sm w-72 focus:border-volt outline-none" />
        <button data-testid="inbox-stop-filter" onClick={() => setStopOnly(!stopOnly)}
          className={`inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-widest border transition-colors ${stopOnly ? "border-red-600 text-red-400" : "border-neutral-700 text-neutral-500 hover:border-neutral-500"}`}>
          <OctagonMinus size={11} /> STOP EVENTS ONLY {data ? `(${data.stop_count})` : ""}
        </button>
        {[["interested", `🟢 INTERESTED (${tc.interested ?? 0})`], ["question", `🟡 QUESTIONS (${tc.question ?? 0})`], ["not_now", `⚪ NOT NOW (${tc.not_now ?? 0})`]].map(([key, label]) => (
          <button key={key} data-testid={`inbox-triage-${key}`} onClick={() => setTriage(triage === key ? "" : key)}
            className={`px-3 py-2 text-[10px] tracking-widest border transition-colors ${triage === key ? TRIAGE_STYLE[key] : "border-neutral-700 text-neutral-500 hover:border-neutral-500"}`}>
            {label}
          </button>
        ))}
        {data && <span className="text-xs text-neutral-500 ml-auto" data-testid="inbox-total">{data.total} messages</span>}
      </div>

      {!data ? (
        <div className="mt-6 text-neutral-500 text-sm">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="mt-6 border border-dashed border-neutral-800 p-10 text-neutral-500 flex items-center gap-3" data-testid="inbox-empty">
          <Inbox size={18} /> No replies yet. When someone answers an outreach email, it shows up here automatically.
        </div>
      ) : (
        <>
          <div className="mt-4 space-y-1.5" data-testid="inbox-list">
            {data.items.map((m, i) => (
              <div key={m.id || i} className={`border px-4 py-3 ${m.stop_detected ? "border-red-900/60 bg-red-950/10" : "border-neutral-800 bg-appcard"}`} data-testid={`inbox-row-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-white font-medium">{m.from}</span>
                  {m.stop_detected
                    ? <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-red-600 text-red-500">AUTO OPTED-OUT</span>
                    : m.inactive_detected
                    ? <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-amber-600 text-amber-500">INACTIVE MAILBOX</span>
                    : m.triage && TRIAGE_STYLE[m.triage]
                    ? <span className={`text-[9px] tracking-widest px-1.5 py-0.5 border ${TRIAGE_STYLE[m.triage]}`} data-testid={`inbox-triage-chip-${i}`}>{m.triage === "not_now" ? "NOT NOW" : m.triage.toUpperCase()}</span>
                    : <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-emerald-600/60 text-emerald-400">REPLY</span>}
                  {m.forwarded_to && (
                    <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-neutral-700 text-neutral-400 inline-flex items-center gap-1">
                      <MailCheck size={9} /> FORWARDED
                    </span>
                  )}
                  <span className="ml-auto text-[10px] text-neutral-600">{m.received_at ? new Date(m.received_at).toLocaleString() : ""}</span>
                </div>
                <div className="text-xs text-neutral-300 mt-1">{m.subject || "(no subject)"}</div>
                {m.snippet && <div className="text-[11px] text-neutral-500 mt-1 line-clamp-2">{m.snippet}</div>}
                <div className="mt-1.5">
                  <a href={`mailto:${m.from}`} className="text-[10px] tracking-widest text-volt hover:underline" data-testid={`inbox-reply-${i}`}>REPLY DIRECTLY →</a>
                </div>
              </div>
            ))}
          </div>
          {data.total > limit && (
            <div className="mt-3 flex items-center gap-3 text-[11px] text-neutral-500">
              <button data-testid="inbox-prev" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40"><ChevronLeft size={12} /></button>
              <span>{skip + 1}–{Math.min(skip + limit, data.total)} of {data.total}</span>
              <button data-testid="inbox-next" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)} className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40"><ChevronRight size={12} /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
